<template>
  <nuxt-link
    v-if="resolvedLink && !isExternalLink && !isInsideStoryblok"
    :to="resolvedLink"
    :target="openNewTab ? '_blank' : '_self'"
  >
    <slot />
  </nuxt-link>
  <a
    v-else-if="isExternalLink && !isInsideStoryblok"
    :href="props.cmsLink?.cached_url"
    :target="openNewTab ? '_blank' : '_self'"
  >
    <slot />
  </a>
  <nuxt-link
    v-else-if="shouldRenderInternalLink && !isInsideStoryblok"
    :to="
      product
        ? toLocalePath(getProductDetailRoute(product))
        : toLocalePath(cachedUrl)
    "
    :target="openNewTab ? '_blank' : '_self'"
  >
    <slot />
  </nuxt-link>
  <span v-else>
    <slot />
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { PropType } from 'vue'
import { useProduct, useProducts } from '#storefront/composables'
import useRouteHelpers from '#layers/base/composables/useRouteHelpers'
import { useStoryblokEditor } from '#layers/base/composables/cms/useStoryblokEditor'
import { getProductDetailRoute } from '~/utils/route'
import type { SbInteractableImage } from '~/cms/storyblok/types/storyblok.d.ts'
import { DEFAULT_PRICE_PROMOTION_KEY } from '#layers/base/constants/withParameters'
import { resolveStoryblokLink } from '#layers/base/utils/storyblok'

const props = defineProps({
  cmsLink: {
    type: Object as PropType<SbInteractableImage['link']>,
    default: () => {},
  },
  to: {
    type: String,
    default: null,
  },
  openNewTab: {
    type: Boolean,
    default: false,
  },
  productId: {
    type: [String, undefined] as PropType<string | undefined>,
    default: undefined,
  },
  orderNumber: {
    type: [String, undefined] as PropType<string | undefined>,
    default: undefined,
  },
})

const { toLocalePath } = useRouteHelpers()
const { isInStoryblokEditor: isInsideStoryblok } = useStoryblokEditor()

const term = props.orderNumber?.replace('-', '')

const { data: productsData } = useProducts(
  {
    params: {
      category: '/',
      perPage: 1,
      with: getProductWith(),
      sort: {},
      where: {
        term,
        disableFuzziness: true,
      },
      pricePromotionKey: DEFAULT_PRICE_PROMOTION_KEY,
    },
    options: { immediate: !!props.orderNumber },
  },
  `product-${props.orderNumber}`,
)

const { data: productData } = useProduct(
  {
    params: {
      id: Number(props.productId),
      with: getProductWith(),
      pricePromotionKey: DEFAULT_PRICE_PROMOTION_KEY,
    },
    options: {
      immediate: !!(
        props.productId &&
        !isNaN(Number(props.productId)) &&
        Number(props.productId) > 0
      ),
    },
  },
  `product-${props.productId}`,
)

const shouldRenderInternalLink = computed(() => {
  if (product.value) {
    return true
  }

  if (props.cmsLink?.linktype === 'story') {
    return !!props.cmsLink.cached_url?.trim()
  }

  if (props.cmsLink?.linktype === 'url') {
    return !!props.cmsLink.url?.trim()
  }

  return false
})

const cachedUrl = computed(() => props.cmsLink?.cached_url || '')

const product = computed(() => {
  if (props.orderNumber) {
    return productsData.value?.products?.[0]
  } else if (props.productId) {
    return productData.value || undefined
  }
  return undefined
})

const isExternalLink = computed(() => {
  return (
    props.cmsLink?.linktype === 'url' &&
    (props.cmsLink.url?.startsWith('http') ||
      props.cmsLink.url?.startsWith('https'))
  )
})

const resolvedLink = computed(() => {
  return resolveStoryblokLink({
    link: props.cmsLink,
    defaultPath: props.to,
  })
})

function getProductWith() {
  // Type assertion needed due to SCAYLE API constraints
  return {
    attributes: {
      withKey: ['name', 'mainOrderNumberList'],
    },
    advancedAttributes: {
      withKey: ['mainOrderNumber'],
    },
  } as any
}
</script>
